// src/redux/stripeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Stripe hesabı oluşturma
export const createStripeAccount = createAsyncThunk(
  "stripe/createAccount",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.post(
        `${API_URL}/therapists/stripe/create-account`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Stripe account creation failed"
      );
    }
  }
);

// Stripe onboarding linkini alma
export const getStripeOnboardingLink = createAsyncThunk(
  "stripe/getOnboardingLink",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/onboarding-link`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get onboarding link"
      );
    }
  }
);

// Stripe hesap durumunu kontrol etme
export const checkStripeAccountStatus = createAsyncThunk(
  "stripe/checkAccountStatus",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/account-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check account status"
      );
    }
  }
);

// Stripe dashboard linkini alma
export const getStripeDashboardLink = createAsyncThunk(
  "stripe/getDashboardLink",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/dashboard-link`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get dashboard link"
      );
    }
  }
);

// Ödeme geçmişini alma
export const getPaymentHistory = createAsyncThunk(
  "stripe/getPaymentHistory",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.get(
        `${API_URL}/therapists/stripe/payment-history?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get payment history"
      );
    }
  }
);

// Kazanç özetini alma
export const getEarnings = createAsyncThunk(
  "stripe/getEarnings",
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      let url = `${API_URL}/therapists/earnings`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get earnings data"
      );
    }
  }
);

// Stripe hesabını silme
export const deleteStripeAccount = createAsyncThunk(
  "stripe/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue("Session not found");
      }
      
      const response = await axios.delete(
        `${API_URL}/therapists/stripe/account`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete Stripe account"
      );
    }
  }
);

const stripeSlice = createSlice({
  name: "stripe",
  initialState: {
    loading: false,
    error: null,
    accountStatus: null,
    onboardingUrl: null,
    dashboardUrl: null,
    paymentHistory: [],
    earnings: null,
    accountCreated: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOnboardingUrl: (state) => {
      state.onboardingUrl = null;
    },
    clearDashboardUrl: (state) => {
      state.dashboardUrl = null;
    },
    resetStripeState: (state) => {
      state.loading = false;
      state.error = null;
      state.accountStatus = null;
      state.onboardingUrl = null;
      state.dashboardUrl = null;
      state.paymentHistory = [];
      state.earnings = null;
      state.accountCreated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Account
      .addCase(createStripeAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStripeAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accountCreated = true;
        state.accountStatus = action.payload;
      })
      .addCase(createStripeAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Onboarding Link
      .addCase(getStripeOnboardingLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStripeOnboardingLink.fulfilled, (state, action) => {
        state.loading = false;
        state.onboardingUrl = action.payload.onboardingUrl;
      })
      .addCase(getStripeOnboardingLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Check Account Status
      .addCase(checkStripeAccountStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkStripeAccountStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.accountStatus = action.payload;
      })
      .addCase(checkStripeAccountStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Dashboard Link
      .addCase(getStripeDashboardLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStripeDashboardLink.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardUrl = action.payload.dashboardUrl;
      })
      .addCase(getStripeDashboardLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Payment History
      .addCase(getPaymentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPaymentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentHistory = action.payload.data;
      })
      .addCase(getPaymentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Earnings
      .addCase(getEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.earnings = action.payload.data;
      })
      .addCase(getEarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Account
      .addCase(deleteStripeAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStripeAccount.fulfilled, (state) => {
        state.loading = false;
        state.accountCreated = false;
        state.accountStatus = null;
      })
      .addCase(deleteStripeAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  clearOnboardingUrl, 
  clearDashboardUrl,
  resetStripeState 
} = stripeSlice.actions;

export default stripeSlice.reducer;